import { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { resolveDiscountAchiever, resolveDiscountAchieverPage } from "lib/core/apiMethods";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import {
  BlockFilter,
  DiscountAchieverBlock,
  DiscountAchieverProduct,
  DiscountAchieverVariant,
  getInventoryCap,
  ImpactedEntity,
  SortBy,
} from "lib/types/discountAchiever";
import { infoToast } from "lib/utils/toasters";

export interface BlockState {
  block: DiscountAchieverBlock;
  sortBy: SortBy;
  filter: BlockFilter;
  selectedItems: Record<string, number>;
  selectedMeta: Record<string, { product: DiscountAchieverProduct; variant: DiscountAchieverVariant }>;
  loadingMore: boolean;
  refreshing: boolean;
  pageError: string | null;
}

interface AchieverState {
  status: "idle" | "loading" | "error" | "ready";
  error: string | null;
  blocks: BlockState[];
  activeBlockIndex: number;
  activeDetailProduct: DiscountAchieverProduct | null;
  /** Recently added selections across all blocks, newest first — drives the footer card-deck.
      Stored as (blockIndex, variantId) keys, not image URLs: two variants of one product share an
      image, so a URL list can't tell "variant removed" from "product no longer selected". Images
      are resolved at render. Lives here (not in the step component) so it survives the
      selection/detail view swap. */
  recentSelections: SelectionKey[];
}

export interface SelectionKey {
  blockIndex: number;
  variantId: string;
}
export const INVENTORY_LIMIT_MESSAGE = "Sorry, we have limited quantity available for this item!";

const DEFAULT_SORT: SortBy = "GAP_CLOSING";
const DEFAULT_FILTER: BlockFilter = { exclude_out_of_stock: true };
const PAGE_SIZE = 20;

const initialState: AchieverState = {
  status: "idle",
  error: null,
  blocks: [],
  activeBlockIndex: 0,
  activeDetailProduct: null,
  recentSelections: [],
};

enum ActionType {
  FETCH_START = "FETCH_START",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_ERROR = "FETCH_ERROR",
  SET_ACTIVE_BLOCK = "SET_ACTIVE_BLOCK",
  PAGE_START = "PAGE_START",
  PAGE_SUCCESS = "PAGE_SUCCESS",
  PAGE_ERROR = "PAGE_ERROR",
  BLOCK_FETCH_START = "BLOCK_FETCH_START",
  BLOCK_FETCH_SUCCESS = "BLOCK_FETCH_SUCCESS",
  BLOCK_FETCH_ERROR = "BLOCK_FETCH_ERROR",
  UPDATE_QTY = "UPDATE_QTY",
  OPEN_DETAIL = "OPEN_DETAIL",
  CLOSE_DETAIL = "CLOSE_DETAIL",
  RESET = "RESET",
}

type Action =
  | { type: ActionType.FETCH_START }
  | { type: ActionType.FETCH_SUCCESS; payload: { blocks: DiscountAchieverBlock[] } }
  | { type: ActionType.FETCH_ERROR; payload: { error: string } }
  | { type: ActionType.SET_ACTIVE_BLOCK; payload: { index: number } }
  | { type: ActionType.PAGE_START; payload: { blockIndex: number } }
  | {
      type: ActionType.PAGE_SUCCESS;
      payload: { blockIndex: number; products: DiscountAchieverProduct[]; pagination: DiscountAchieverBlock["pagination"] };
    }
  | { type: ActionType.PAGE_ERROR; payload: { blockIndex: number; error: string } }
  | { type: ActionType.BLOCK_FETCH_START; payload: { blockIndex: number; sortBy: SortBy; filter: BlockFilter } }
  | { type: ActionType.BLOCK_FETCH_SUCCESS; payload: { blockIndex: number; block: DiscountAchieverBlock } }
  | { type: ActionType.BLOCK_FETCH_ERROR; payload: { blockIndex: number; error: string } }
  | {
      type: ActionType.UPDATE_QTY;
      payload: { blockIndex: number; variantId: string; delta: number; product?: DiscountAchieverProduct;
        variant?: DiscountAchieverVariant;};
    }
  | { type: ActionType.OPEN_DETAIL; payload: { product: DiscountAchieverProduct } }
  | { type: ActionType.CLOSE_DETAIL }
  | { type: ActionType.RESET };

const updateBlockAt = (blocks: BlockState[], index: number, patch: Partial<BlockState>): BlockState[] =>
  blocks.map((blockState, i) => (i === index ? { ...blockState, ...patch } : blockState));

function reducer(state: AchieverState, action: Action): AchieverState {
  switch (action.type) {
    case ActionType.FETCH_START:
      return { ...state, status: "loading", error: null };

    case ActionType.FETCH_SUCCESS:
      return {
        ...state,
        status: "ready",
        error: null,
        activeBlockIndex: 0,
        blocks: action.payload.blocks.map((block) => ({
          block,
          sortBy: DEFAULT_SORT,
          filter: DEFAULT_FILTER,
          selectedItems: {},
          selectedMeta: {},
          loadingMore: false,
          refreshing: false,
          pageError: null,
        })),
        recentSelections: [],
      };

    case ActionType.FETCH_ERROR:
      return { ...state, status: "error", error: action.payload.error };

    case ActionType.SET_ACTIVE_BLOCK:
      return { ...state, activeBlockIndex: action.payload.index };

    case ActionType.PAGE_START:
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, action.payload.blockIndex, { loadingMore: true, pageError: null }),
      };

    case ActionType.PAGE_SUCCESS: {
      const { blockIndex, products, pagination } = action.payload;
      const target = state.blocks[blockIndex];
      if (!target) return state;
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, blockIndex, {
          loadingMore: false,
          block: { ...target.block, products: [...target.block.products, ...products], pagination },
        }),
      };
    }

    case ActionType.PAGE_ERROR:
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, action.payload.blockIndex, {
          loadingMore: false,
          pageError: action.payload.error,
        }),
      };

    case ActionType.BLOCK_FETCH_START:
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, action.payload.blockIndex, {
          sortBy: action.payload.sortBy,
          filter: action.payload.filter,
          refreshing: true,
          pageError: null,
        }),
      };

    case ActionType.BLOCK_FETCH_SUCCESS:
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, action.payload.blockIndex, {
          block: action.payload.block,
          refreshing: false,
          pageError: null,
        }),
      };

    case ActionType.BLOCK_FETCH_ERROR:
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, action.payload.blockIndex, {
          refreshing: false,
          pageError: action.payload.error,
        }),
      };

    case ActionType.UPDATE_QTY: {
      const { blockIndex, variantId, delta, product, variant } = action.payload;
      const target = state.blocks[blockIndex];
      if (!target) return state;
      const nextQty = Math.max(0, (target.selectedItems[variantId] ?? 0) + delta);
      const selectedItems = { ...target.selectedItems };
      const selectedMeta = { ...target.selectedMeta };
      if (nextQty === 0) {
        delete selectedItems[variantId];
        delete selectedMeta[variantId];
      } else {
        selectedItems[variantId] = nextQty;
        if (product && variant) selectedMeta[variantId] = { product, variant };
      }
      // Deck order: newest add moves to the front; a variant only leaves once its qty hits 0.
      const isSame = (key: SelectionKey) => key.blockIndex === blockIndex && key.variantId === variantId;
      const others = state.recentSelections.filter((key) => !isSame(key));
      const recentSelections =
        nextQty === 0 ? others : delta > 0 ? [{ blockIndex, variantId }, ...others] : state.recentSelections;
      return {
        ...state,
        blocks: updateBlockAt(state.blocks, blockIndex, { selectedItems, selectedMeta }),
        recentSelections,
      };
    }

    case ActionType.OPEN_DETAIL:
      return { ...state, activeDetailProduct: action.payload.product };

    case ActionType.CLOSE_DETAIL:
      return { ...state, activeDetailProduct: null };
    case ActionType.RESET:
        return initialState;
    default:
      return state;
  }
}

interface DiscountAchieverContextValue {
  state: AchieverState;
  actions: {
    fetchInitialBlocks: (impactedEntities: ImpactedEntity[]) => Promise<void>;
    fetchNextPage: (blockIndex: number) => Promise<void>;
    setSort: (blockIndex: number, sortBy: SortBy) => Promise<void>;
    setFilter: (blockIndex: number, filter: BlockFilter) => Promise<void>;
    setActiveBlock: (index: number) => void;
    updateQty: (blockIndex: number, variantId: string, delta: number) => void;
    openDetail: (product: DiscountAchieverProduct) => void;
    closeDetail: () => void;
    reset: () => void;
  };
}

const Context = createContext<DiscountAchieverContextValue | null>(null);

export const DiscountAchieverProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    state: { merchant, accountId },
  } = useMerchantContext();
  const merchantId = merchant?.merchantId?.trim() || accountId?.trim() || "";
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const fetchInitialBlocks = useCallback(
    async (impactedEntities: ImpactedEntity[]) => {
      if (!merchantId) {
        dispatch({ type: ActionType.FETCH_ERROR, payload: { error: "Failed to load products" } });
        return;
      }
      dispatch({ type: ActionType.FETCH_START });
      try {
        const response = await resolveDiscountAchiever(merchantId, {
          impacted_entities: impactedEntities,
          sort_by: DEFAULT_SORT,
          filter: DEFAULT_FILTER,
          page_size: PAGE_SIZE,
        });
        dispatch({ type: ActionType.FETCH_SUCCESS, payload: { blocks: response.blocks } });
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_LOADED,
          eventType: "load",
          metaData: { discountAchieverData: { blockCount: response.blocks.length, impactedEntities } },
        });
      } catch (e) {
        dispatch({ type: ActionType.FETCH_ERROR, payload: { error: "Failed to load products" } });
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_LOAD_FAILED,
          eventType: "load",
          metaData: { discountAchieverData: { impactedEntities } },
        });
      }
    },
    [merchantId, sendAnalyticsEvent],
  );

  const fetchNextPage = useCallback(
    async (blockIndex: number) => {
      if (!merchantId) return;
      const target = state.blocks[blockIndex];
      if (!target || !target.block.pagination.has_more || !target.block.pagination.next_cursor) return;
      dispatch({ type: ActionType.PAGE_START, payload: { blockIndex } });
      try {
        const response = await resolveDiscountAchieverPage(merchantId, {
          cursor: target.block.pagination.next_cursor,
        });
        dispatch({
          type: ActionType.PAGE_SUCCESS,
          payload: { blockIndex, products: response.products, pagination: response.pagination },
        });
      } catch (e) {
        dispatch({ type: ActionType.PAGE_ERROR, payload: { blockIndex, error: "Failed to load more products" } });
      }
    },
    [state.blocks, merchantId],
  );

  const refetchBlock = useCallback(
    async (blockIndex: number, sortBy: SortBy, filter: BlockFilter) => {
      const target = state.blocks[blockIndex];
      if (!target || !merchantId) return;
      dispatch({ type: ActionType.BLOCK_FETCH_START, payload: { blockIndex, sortBy, filter } });
      try {
        const response = await resolveDiscountAchiever(merchantId, {
          impacted_entities: [target.block.impacted_entity],
          sort_by: sortBy,
          filter,
          page_size: PAGE_SIZE,
        });
        const block = response.blocks[0];
        if (!block) {
          dispatch({ type: ActionType.BLOCK_FETCH_ERROR, payload: { blockIndex, error: "No products found" } });
          return;
        }
        dispatch({ type: ActionType.BLOCK_FETCH_SUCCESS, payload: { blockIndex, block } });
      } catch (e) {
        dispatch({ type: ActionType.BLOCK_FETCH_ERROR, payload: { blockIndex, error: "Failed to update products" } });
      }
    },
    [state.blocks, merchantId],
  );

  const setSort = useCallback(
    (blockIndex: number, sortBy: SortBy) => {
      const filter = state.blocks[blockIndex]?.filter ?? DEFAULT_FILTER;
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_SORT_CHANGED,
        eventType: "click",
        metaData: { discountAchieverData: { blockIndex, sortBy } },
      });
      return refetchBlock(blockIndex, sortBy, filter);
    },
    [state.blocks, refetchBlock, sendAnalyticsEvent],
  );

  const setFilter = useCallback(
    (blockIndex: number, filter: BlockFilter) => {
      const sortBy = state.blocks[blockIndex]?.sortBy ?? DEFAULT_SORT;
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_DISCOUNT_ACHIEVER_FILTER_APPLIED,
        eventType: "click",
        metaData: {
          discountAchieverData: {
            blockIndex,
            priceBand: filter.selected_price_band ?? null,
            cleared: !filter.selected_price_band,
          },
        },
      });
      return refetchBlock(blockIndex, sortBy, filter);
    },
    [state.blocks, refetchBlock, sendAnalyticsEvent],
  );

  const setActiveBlock = useCallback((index: number) => {
    dispatch({ type: ActionType.SET_ACTIVE_BLOCK, payload: { index } });
  }, []);

  const updateQty = useCallback(
    (blockIndex: number, variantId: string, delta: number) => {
      const blockState = state.blocks[blockIndex];
      // Prefer the live product; fall back to the add-time snapshot so a sort/filter refetch that
      // dropped this variant from block.products still resolves it (keeps the inventory cap working).
      const liveProduct = blockState?.block.products.find((item) =>
        item.variants.some((variant) => variant.variant_id === variantId),
      );
      const meta = blockState?.selectedMeta[variantId];
      const product = liveProduct ?? meta?.product;
      const variant = liveProduct?.variants.find((item) => item.variant_id === variantId) ?? meta?.variant;
      const currentQty = blockState?.selectedItems[variantId] ?? 0;

      let appliedDelta = delta;
      // Cap additions to remaining stock (only when the variant blocks overselling); removals pass through
      if (delta > 0 && variant) {
        const remaining = Math.max(0, getInventoryCap(variant) - currentQty);
        appliedDelta = Math.min(delta, remaining);
        if (appliedDelta < delta) infoToast(INVENTORY_LIMIT_MESSAGE);
        if (appliedDelta === 0) return;
      }

      dispatch({
        type: ActionType.UPDATE_QTY,
        payload: { blockIndex, variantId, delta: appliedDelta, product, variant },
      });
      sendAnalyticsEvent({
        eventName:
          appliedDelta > 0
            ? analyticsEvents.FLO_DISCOUNT_ACHIEVER_ITEM_ADDED
            : analyticsEvents.FLO_DISCOUNT_ACHIEVER_ITEM_REMOVED,
        eventType: "click",
        metaData: { discountAchieverData: { blockIndex, variantId, productId: product?.product_id } },
      });
    },
    [state.blocks, sendAnalyticsEvent],
  );

  const openDetail = useCallback((product: DiscountAchieverProduct) => {
    dispatch({ type: ActionType.OPEN_DETAIL, payload: { product } });
  }, []);

  const closeDetail = useCallback(() => {
    dispatch({ type: ActionType.CLOSE_DETAIL });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: ActionType.RESET });
  }, []);

  const actions = useMemo(
    () => ({
      fetchInitialBlocks,
      fetchNextPage,
      setSort,
      setFilter,
      setActiveBlock,
      updateQty,
      openDetail,
      closeDetail,
      reset
    }),
    [fetchInitialBlocks, fetchNextPage, setSort, setFilter, setActiveBlock, updateQty, openDetail, closeDetail],
  );

  return <Context.Provider value={{ state, actions }}>{children}</Context.Provider>;
};

export function useDiscountAchieverContext() {
  const context = useContext(Context);
  if (!context) throw new Error("useDiscountAchieverContext must be used within a DiscountAchieverProvider");
  return context;
}
